import { formatRecoverySavedAt, readRecoveryCodesStatus } from '@src/utils/recoveryCodes';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner, Stack, Typography } from '@src/components';
import { closeDialog, showDialog } from '@src/components/Dialog/Dialog';
import { AccessCodes } from '@src/pages/BoxPage/AccessCodes';
import './PremiumRecoveryPrompt.css';

// Keep an in-flight issuance across React remounts, never in persistent storage.
type Issuance = { recoveryCodes: string[]; recoveryCodesSavedAt: string | null };
const requests = new Map<number, Promise<Issuance>>();
function issue(id: number) {
  if (requests.has(id)) return requests.get(id)!;
  const request = fetch('/api/access/recovery-codes/premium-unacknowledged', {
    method: 'POST', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' }, body: '{}',
  }).then(async response => {
    if (!response.ok) throw new Error('Recovery unavailable');
    const data = await response.json();
    if (!Array.isArray(data.recoveryCodes)) throw new Error('Recovery unavailable');
    if (!data.recoveryCodes.length && !data.recoveryCodesSavedAt) throw new Error('Recovery unavailable');
    return data as Issuance;
  }).finally(() => { if (requests.get(id) === request) requests.delete(id); });
  requests.set(id, request);
  return request;
}
function RecoveryBody({ userId, onSaved }: { userId: number; onSaved: (savedAt: string) => void }) {
  const { t } = useTranslation();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionsContainer, setActionsContainer] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    let active = true;
    issue(userId).then(value => {
      if (!active) return;
      if (value.recoveryCodesSavedAt) { onSaved(value.recoveryCodesSavedAt); closeDialog(); }
      else setCodes(value.recoveryCodes);
    })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [userId, onSaved]);
  function finish(savedAt?: string) {
    if (!savedAt) return;
    requests.delete(userId);
    onSaved(savedAt);
    closeDialog();
  }
  async function generate() {
    if (busy) return;
    setBusy(true); setError(false);
    try {
      const value = await issue(userId);
      if (value.recoveryCodesSavedAt) finish(value.recoveryCodesSavedAt);
      else setCodes(value.recoveryCodes);
    }
    catch { setError(true); }
    finally { setBusy(false); }
  }
  return <div className="premium-recovery">
    <div className="premium-recovery__content">
      <Stack gap={4} wide>
        <Typography as="p" role="body/base" hidden={!codes?.length}>{t('premiumBuy.recoveryPrompt')}</Typography>
        {codes?.length ? <AccessCodes kind="account" variant="flat"
          subject={t('account.recoveryTitle')} url={window.location.href} codes={codes}
          copyIntroduction={t('premiumBuy.recoveryCopy', { site: window.location.host })}
          actionsContainer={actionsContainer} onContinue={finish} /> : error ? <Typography as="p" role="body/base">{t('premiumBuy.recoveryError')}</Typography> : <Spinner label={t('account.loading')} />}
      </Stack>
    </div>
    <div className="premium-recovery__actions" ref={setActionsContainer}>
      {!codes?.length && error && <Button disabled={busy} onClick={() => void generate()}
        testId="premium-recovery-retry">{t('account.recoveryCheckAgain')}</Button>}
    </div>
  </div>;
}
export function PremiumRecoveryPrompt({ userId, onReady }: { userId: number; onReady?: (userId: number) => void }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<{ userId: number; savedAt: string | null } | null>(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    void readRecoveryCodesStatus().then(data => {
      if (active) setStatus({ userId, savedAt: data.recoveryCodesSavedAt });
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [userId, retry]);
  const onSaved = useCallback((savedAt: string) => setStatus({ userId, savedAt }), [userId]);
  const ready = status?.userId === userId;
  const savedAt = ready ? status.savedAt : null;
  useEffect(() => {
    if (ready && savedAt) onReady?.(userId);
  }, [ready, savedAt, userId, onReady]);
  useEffect(() => {
    if (!ready || savedAt) return;
    showDialog({ title: t('premiumBuy.keepAccessTitle'), hideClose: true,
      preventDismiss: true, mobileFullScreen: true, testId: 'premium-recovery',
      children: <RecoveryBody userId={userId} onSaved={onSaved} /> });
    return () => closeDialog();
  }, [userId, t, ready, savedAt, onSaved]);
  if (savedAt) return <Typography as="p" role="body/base" testId="recovery-saved-at">
    {t('account.recoverySavedAt', { date: formatRecoverySavedAt(savedAt, i18n.language) })}
  </Typography>;
  if (error) return <Stack gap={3}>
    <Typography as="p" role="body/base">{t('premiumBuy.recoveryError')}</Typography>
    <Button onClick={() => { setError(false); setRetry(value => value + 1); }}>{t('account.recoveryCheckAgain')}</Button>
  </Stack>;
  return null;
}
